import { Stack, Tooltip, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";

import { CARD_WIDTH, CARD_MAX_WIDTH, CLIP_PATHS } from "./constants";
import { DragonTileChips } from "./DragonTileChips";
import { FrameCardActions } from "./FrameCardActions";
import { FrameCardPreview } from "./FrameCardPreview";
import { MediumPicker } from "./MediumPicker";
import { MediumRevenueMetric } from "./MediumRevenueMetric";
import type { FrameCardBaseProps, SupportedMedium } from "./types";
import { useFrameMediums } from "./useFrameMediums";

import { CheckCircleOutlineIcon } from "@/icons";

// FrameCard - Displays a tile with preview and metadata

/*
  Needs a way to be a link w/ the active state
*/
export default function FrameCard({
  frame,
  siteId,
  variantPreviews,
  isActionable = true,
  onClick,
  showDragonTileChips = true,
  filterMedium,
}: FrameCardBaseProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [hasOpenModal, setHasOpenModal] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const nameRef = useRef<HTMLSpanElement>(null);
  const { selectedMedium, setSelectedMedium } = useFrameMediums(frame);

  // Set selected medium to match filter when filter changes
  useEffect(() => {
    if (filterMedium) {
      setSelectedMedium(filterMedium as SupportedMedium);
    }
  }, [filterMedium, setSelectedMedium]);

  useEffect(() => {
    const el = nameRef.current;
    if (el) {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    }
  }, [frame.name]);

  return (
    <Stack
      sx={{
        width: CARD_WIDTH,
        maxWidth: CARD_MAX_WIDTH,
        position: "relative",
      }}
      onMouseEnter={() => isActionable && setIsHovered(true)}
      onMouseLeave={() => isActionable && setIsHovered(false)}
      onClick={() => onClick?.(frame)}
    >
      <FrameCardPreview
        frame={frame}
        siteId={siteId}
        selectedMedium={selectedMedium}
        variantPreviews={variantPreviews}
        autoScroll={isHovered}
      />
      {(isHovered || hasOpenModal) && isActionable && (
        <FrameCardActions
          frame={frame}
          siteId={siteId}
          selectedMedium={selectedMedium}
          onModalOpenChange={setHasOpenModal}
        />
      )}

      <Stack
        sx={{
          pt: 4,
          pb: 5,
          px: 6,
          backgroundColor: "white",
          clipPath: CLIP_PATHS.bottom,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={3}>
            {showDragonTileChips && (
              <DragonTileChips
                innerTileId={frame.inner_tile}
                siteId={siteId}
                assignFrameId={isActionable ? frame.id : undefined}
              />
            )}
            <MediumPicker
              frame={frame}
              selectedMedium={selectedMedium}
              onMediumSelect={setSelectedMedium}
              variant="queen"
              disabled={!isActionable}
            />
          </Stack>
          <Typography variant="body3" sx={{ whiteSpace: "nowrap" }}>
            {frame.modify_date
              ? dayjs.utc(frame.modify_date).local().format("M/D/YY h:mm A z")
              : ""}
          </Typography>
        </Stack>

        <Tooltip title={isTruncated ? frame.name : ""} placement="bottom">
          <Typography
            ref={nameRef}
            variant="subtitle1"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              py: 2,
            }}
          >
            {/* Keyed on is_active only — it's the backend-authoritative flag;
                approval_status is FE-only and can lag behind. */}
            {frame.is_active && (
              <CheckCircleOutlineIcon
                sx={{
                  color: "success.main",
                  fontSize: "1em",
                  verticalAlign: "middle",
                  mr: 2,
                  mb: "1px",
                }}
              />
            )}
            {frame.name}
          </Typography>
        </Tooltip>

        <MediumRevenueMetric frame={frame} selectedMedium={selectedMedium} />
      </Stack>
    </Stack>
  );
}
